import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Download,
  CheckCircle2,
  Smartphone,
  X,
  RefreshCcw,
} from "lucide-react";

const INSTALL_FLAG_KEY = "pwa-installed-imene-yahiaoui";

export default function InstallButton() {
  const { t, i18n } = useTranslation();

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isInstalledOnDevice, setIsInstalledOnDevice] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [showResetModal, setShowResetModal] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkInstallState = async () => {
      try {
        const mobileCheck = /Android|iPhone|iPad|iPod/i.test(
          window.navigator.userAgent,
        );

        const standalone =
          window.matchMedia("(display-mode: standalone)").matches ||
          window.navigator.standalone === true;

        const localInstalled =
          window.localStorage.getItem(INSTALL_FLAG_KEY) === "true";

        if (!isMounted) return;

        setIsMobile(mobileCheck);
        setIsStandalone(standalone);
        setIsInstalledOnDevice(localInstalled);

        if ("getInstalledRelatedApps" in navigator) {
          try {
            const relatedApps = await navigator.getInstalledRelatedApps();
            const hasInstalledApp =
              Array.isArray(relatedApps) && relatedApps.length > 0;

            if (!isMounted) return;

            if (hasInstalledApp) {
              setIsInstalledOnDevice(true);
              window.localStorage.setItem(INSTALL_FLAG_KEY, "true");
            }
          } catch (error) {
            console.error("getInstalledRelatedApps error:", error);
          }
        }
      } finally {
        if (isMounted) {
          setIsChecking(false);
        }
      }
    };

    checkInstallState();

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const onAppInstalled = () => {
      setIsStandalone(true);
      setIsInstalledOnDevice(true);
      setDeferredPrompt(null);
      window.localStorage.setItem(INSTALL_FLAG_KEY, "true");
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      isMounted = false;
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;

      if (choice.outcome === "accepted") {
        window.localStorage.setItem(INSTALL_FLAG_KEY, "true");
        setIsInstalledOnDevice(true);
      }

      setDeferredPrompt(null);
    } catch (error) {
      console.error("Install prompt error:", error);
    }
  };

  const handleResetInstalledState = () => {
    window.localStorage.removeItem(INSTALL_FLAG_KEY);
    setIsInstalledOnDevice(false);
    setIsStandalone(false);
    setShowResetModal(false);
    window.location.reload();
  };

  const isInstalled = isStandalone || isInstalledOnDevice;
  const isRtl = i18n.language === "ar";

  const descriptionText = isInstalled
    ? t("installApp.description_installed")
    : isMobile
      ? t("installApp.description_mobile")
      : t("installApp.description_desktop");

  const fallbackButtonText = isMobile
    ? t("installApp.button_mobile")
    : t("installApp.button_desktop");

  if (isChecking) {
    return (
      <div className="install-app-card">
        <div className="install-app-icon">
          <Smartphone size={22} />
        </div>

        <div className="install-app-content">
          <span className="install-app-label">{t("installApp.label")}</span>
          <p className="install-app-title">{t("installApp.title_install")}</p>
          <p className="install-app-desc">{descriptionText}</p>
        </div>

        <div className="install-app-action">
          <button type="button" className="install-app-btn muted" disabled>
            <Smartphone size={18} />
            ...
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="install-app-card">
        <div className="install-app-icon">
          {isInstalled ? <CheckCircle2 size={22} /> : <Smartphone size={22} />}
        </div>

        <div className="install-app-content">
          <span className="install-app-label">{t("installApp.label")}</span>

          <p className="install-app-title">
            {isInstalled
              ? t("installApp.title_installed")
              : t("installApp.title_install")}
          </p>

          <p className="install-app-desc">{descriptionText}</p>

          {isInstalled && !isStandalone && (
            <button
              type="button"
              className="install-app-reset"
              onClick={() => setShowResetModal(true)}
            >
              <RefreshCcw size={14} />
              {t("installApp.reset_link")}
            </button>
          )}
        </div>

        <div className="install-app-action">
          {isInstalled ? (
            <button
              type="button"
              className="install-app-btn installed"
              disabled
            >
              <CheckCircle2 size={18} />
              {t("installApp.button_installed")}
            </button>
          ) : deferredPrompt ? (
            <button
              type="button"
              className="install-app-btn"
              onClick={handleInstall}
            >
              <Download size={18} />
              {t("installApp.button_install")}
            </button>
          ) : (
            <button type="button" className="install-app-btn muted" disabled>
              <Smartphone size={18} />
              {fallbackButtonText}
            </button>
          )}
        </div>
      </div>

      {showResetModal && (
        <div
          className="install-modal-overlay"
          onClick={() => setShowResetModal(false)}
        >
          <div
            className="install-modal"
            dir={isRtl ? "rtl" : "ltr"}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="install-modal-close"
              onClick={() => setShowResetModal(false)}
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <div className="install-modal-badge">
              <RefreshCcw size={18} />
            </div>

            <h3 className="install-modal-title">
              {t("installApp.reset_title")}
            </h3>

            <p className="install-modal-text">{t("installApp.reset_text")}</p>

            <div className="install-modal-actions">
              <button
                type="button"
                className="install-modal-btn secondary"
                onClick={() => setShowResetModal(false)}
              >
                {t("installApp.reset_cancel")}
              </button>

              <button
                type="button"
                className="install-modal-btn primary"
                onClick={handleResetInstalledState}
              >
                {t("installApp.reset_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
